import {
  Component,
  OnInit,
  OnDestroy,
  ElementRef,
  HostListener,
  ViewChild,
  AfterViewChecked,
} from '@angular/core';
import {
  Firestore,
  Timestamp,
  collection,
  collectionData,
  query,
  where,
  getDocs,
  doc,
  updateDoc,
  getDoc,
} from '@angular/fire/firestore';
import { Auth } from '@angular/fire/auth';
import { Functions, httpsCallable } from '@angular/fire/functions';
import { MatPaginator } from '@angular/material/paginator';
import { UxserviceService } from 'src/app/shared/services/uxservice.service';
import { loadStripe } from '@stripe/stripe-js';
import { environment } from 'src/app/environments/environment';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { PlanService, VendorPermissions } from 'src/app/subscription-management/plan-services/plan.service';
import { VendorContextService } from 'src/app/shared/services/vendor-context.service';

export interface ListedEquipmentItem {
  id: string;
  name?: string;
  equipmentName?: string;
  images?: string[];
  emirate?: string;
  categoryId?: string;
  model?: string;
  year_of_manufacture?: string;
  country_of_origin?: string;
  status?: string;
  /** Simplified: 'active' | 'inactive' | 'booked' — no more 'expired' */
  listingStatus?: 'active' | 'inactive' | 'booked';
  isMarketplaceActive?: boolean;
  [key: string]: any;
}

@Component({
  selector: 'app-listed-equipment',
  templateUrl: './listed-equipment.component.html',
  styleUrls: ['./listed-equipment.component.scss'],
})
export class ListedEquipmentComponent implements OnInit, AfterViewChecked, OnDestroy {

  private static cachedEquipment: ListedEquipmentItem[] = [];
  private static cacheLoaded = false;

  public static clearCache() {
    ListedEquipmentComponent.cachedEquipment = [];
    ListedEquipmentComponent.cacheLoaded = false;
  }

  viewMode: 'grid' | 'list' = 'grid';
  isLoading = true;

  /**
   * Tri-state permission gate.
   * 'loading' → waiting for plan permissions
   * 'granted' → module enabled, show content
   * 'denied'  → upgrade screen
   */
  permissionState: 'loading' | 'granted' | 'denied' = 'loading';

  private destroy$ = new Subject<void>();
  private effectiveUid = '';

  dataSource: ListedEquipmentItem[] = [];
  allEquipment: ListedEquipmentItem[] = [];

  // ── Pagination ────────────────────────────────────────────────────────────
  totalItems = 0;
  pageSize = 8;
  pageIndex = 0;
  pageSizeOptions = [8, 16, 32];

  // ── Filters ───────────────────────────────────────────────────────────────
  searchTerm = '';
  filterEmirate = '';
  listingFilter: 'all' | 'active' | 'inactive' | 'booked' = 'all';
  showFilterPanel = false;

  categories: any[] = [];
  currentCategoryFilter = '';
  categoryCounts: Record<string, number> = {};

  tempCategoryFilter = '';
  tempEmirateFilter = '';
  tempListingFilter: 'all' | 'active' | 'inactive' | 'booked' = 'all';
  showAllCategories = false;

  // ── Listing Quota / Slots ─────────────────────────────────────────────────
  /** Free slots included in the plan */
  listedEquipmentCap = 0;
  /** Trial-enabled plan flag */
  trialEnabled = false;
  /** Trial slot cap for free trial users */
  trialVehicleCap = 0;
  /** Purchased extra slots stored in users-list/{uid}.extraListingSlots */
  extraListingSlots = 0;
  /** Per-slot fee in AED — used for paid listing activation */
  listingFeePerSlot = 0;
  /** Plan name for display */
  currentPlanName = '';
  /** Plan ID of the current plan */
  currentPlanId = '';
  /** Listing duration in days for new marketplace entries */
  listingDays = 0;

  /** Equipment pending Stripe activation */
  pendingEquipmentId: string | null = null;
  pendingEquipmentName = '';

  get totalSlots(): number {
    const baseCap = this.trialEnabled && this.currentPlanId === 'free_trial'
      ? this.trialVehicleCap
      : this.listedEquipmentCap;
    return baseCap + this.extraListingSlots;
  }

  get usedSlots(): number {
    return this.allEquipment.filter(e => e.isMarketplaceActive === true).length;
  }

  get freeSlots(): number {
    return Math.max(0, this.totalSlots - this.usedSlots);
  }

  get slotPercentage(): number {
    if (this.totalSlots === 0) return 100;
    return Math.min(100, Math.round((this.usedSlots / this.totalSlots) * 100));
  }

  // ── Buy More Slots Modal ──────────────────────────────────────────────────
  showBuyMoreModal = false;
  isBuyMoreProcessing = false;
  isBuyMoreInitializing = false;
  buyMorePaymentError = '';
  buyMorePaymentSuccess = false;
  buyMoreCount = 1;

  _buyMoreClientSecret = '';
  private _stripe: any = null;
  private _cardElement: any = null;
  private _cardMounted = false;
  private _cardShouldMount = false;
  private _buyMoreSubmitted = false;

  get buyMoreTotal(): number {
    return this.buyMoreCount * this.listingFeePerSlot;
  }

  get isBuyMoreLocked(): boolean {
    return this._buyMoreSubmitted;
  }

  emirates = [
    { name: 'Abu Dhabi' },
    { name: 'Dubai' },
    { name: 'Sharjah' },
    { name: 'Ajman' },
    { name: 'Fujairah' },
    { name: 'Umm Al Quwain' },
    { name: 'Ras Al Khaimah' },
  ];

  @ViewChild(MatPaginator) paginator!: MatPaginator;

  constructor(
    private firestore: Firestore,
    private auth: Auth,
    private ux: UxserviceService,
    private eRef: ElementRef,
    private functions: Functions,
    private router: Router,
    private planService: PlanService,
    private vendorCtx: VendorContextService,
  ) {
    if (ListedEquipmentComponent.cacheLoaded) {
      this.allEquipment = [...ListedEquipmentComponent.cachedEquipment];
      this.isLoading = false;
      this.dataSource = [...this.allEquipment];
    }
  }

  goToAddEquipment(): void {
    this.router.navigate(['/business/equipment/add']);
  }

  editEquipment(eq: ListedEquipmentItem): void {
    this.router.navigate(['/business/listed-equipment/edit', eq.id]);
  }

  // ── Mount Stripe card when DOM node is ready ───────────────────────────────
  ngAfterViewChecked(): void {
    if (this._cardShouldMount && !this._cardMounted && this._stripe && this._cardElement) {
      const el = document.getElementById('buy-more-card-element');
      if (el) {
        this._cardElement.mount('#buy-more-card-element');
        this._cardMounted = true;
        this._cardShouldMount = false;
      }
    }
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    if (this.showFilterPanel) {
      const el = this.eRef.nativeElement.querySelector('.filter-wrapper');
      if (el && !el.contains(event.target as Node)) {
        this.showFilterPanel = false;
      }
    }
  }

  // ── Init ──────────────────────────────────────────────────────────────────
  async ngOnInit(): Promise<void> {
    const user = this.auth.currentUser;
    if (!user) {
      this.ux.closeOverlay();
      this.isLoading = false;
      this.ux.showError('You must be logged in.');
      return;
    }

    if (!ListedEquipmentComponent.cacheLoaded) {
      this.isLoading = true;
      await this.ux.showOverlay('Loading listed equipment...');
    } else {
      this.isLoading = false;
    }

    // Load categories
    const catRef = collection(this.firestore, 'vehicle-categories');
    collectionData(catRef, { idField: 'id' }).subscribe(
      (cats) => (this.categories = cats),
    );

    // Subscribe to plan permissions — no separate Firestore reads needed
    this.planService.permissions$
      .pipe(takeUntil(this.destroy$))
      .subscribe((perms: VendorPermissions) => {
        this.permissionState = perms?.listedEquipmentModuleEnabled ? 'granted' : 'denied';
        this.listingFeePerSlot = perms?.listingFee ?? 0;
        this.listedEquipmentCap = perms?.listedEquipmentCap ?? 0;
        this.trialEnabled = perms?.trialEnabled ?? false;
        this.trialVehicleCap = perms?.trialVehicleCap ?? 0;
        this.currentPlanName = perms?.currentPlanName ?? '';
        this.currentPlanId = perms?.currentPlanId ?? '';
        this.listingDays = perms?.listingDays ?? 0;
      });

    await this.loadData(false);
  }

  // ── Load equipment ────────────────────────────────────────────────────────
  async loadData(forceReload = false): Promise<void> {
    if (!forceReload && ListedEquipmentComponent.cacheLoaded && this.allEquipment.length > 0) {
      this.allEquipment = [...ListedEquipmentComponent.cachedEquipment];
      this.recomputeCategoryCounts();
      this.applyFilters();
      this.isLoading = false;
      this.ux.closeOverlay();
      return;
    }

    const effectiveUid = await this.vendorCtx.getEffectiveUid();
    if (!effectiveUid) return;
    this.effectiveUid = effectiveUid;

    this.isLoading = true;

    try {
      // Read extra purchased slots from user document
      const userSnap = await getDoc(doc(this.firestore, 'users-list', effectiveUid));
      if (userSnap.exists()) {
        const userData = userSnap.data() as any;
        this.extraListingSlots = userData.extraListingSlots ?? 0;
      }

      const snap = await getDocs(
        query(collection(this.firestore, 'equipment-list'), where('bywhom', '==', effectiveUid)),
      );

      const now = new Date();

      const all: ListedEquipmentItem[] = snap.docs.map(d => {
        const data = d.data() as any;

        // Auto-migrate legacy status
        if (data.status === 'Available') {
          updateDoc(doc(this.firestore, 'equipment-list', d.id), { status: 'Active' })
            .catch(err => console.error('Auto-migration failed for', d.id, err));
          data.status = 'Active';
        }

        // Simplified listing status — no expiry logic
        let listingStatus: ListedEquipmentItem['listingStatus'] = 'inactive';
        const bookingEnd: Date | undefined = data.bookingEndDate
          ? (data.bookingEndDate as any)?.toDate?.()
          : undefined;

        if (bookingEnd && bookingEnd > now) {
          listingStatus = 'booked';
        } else if (data.isMarketplaceActive === true) {
          listingStatus = 'active';
        }

        return {
          id: d.id,
          ...data,
          listingStatus,
        } as ListedEquipmentItem;
      });

      // Sort alphabetically
      all.sort((a, b) =>
        (a.name || '').trim().toLowerCase().localeCompare((b.name || '').trim().toLowerCase())
      );

      // Only show vehicles that are currently active in the marketplace.
      this.allEquipment = all.filter(
        e =>
          !(e['isFromVendorBooking'] === true && (!e['images'] || (e['images'] as any[]).length === 0)) &&
          e['isListed'] === true &&
          e['isMarketplaceActive'] === true
      );

      this.recomputeCategoryCounts();
      ListedEquipmentComponent.cachedEquipment = [...this.allEquipment];
      ListedEquipmentComponent.cacheLoaded = true;
      this.applyFilters();

    } catch (e) {
      console.error('Error loading listed equipment:', e);
      this.ux.showError('Failed to load equipment.');
    } finally {
      this.isLoading = false;
      this.ux.closeOverlay();
    }
  }

  // ── Core marketplace actions ───────────────────────────────────────────────

  /**
   * Shows a vehicle in the marketplace.
   * If free slots are available → direct Firestore update (no payment).
   * If cap hit → opens the "Buy More Slots" modal.
   */
  async showInMarketplace(eq: ListedEquipmentItem): Promise<void> {
    if (eq.listingStatus === 'booked') return;
    if (eq.isMarketplaceActive) return;

    if (this.freeSlots <= 0) {
      if (this.listingFeePerSlot > 0) {
        this.pendingEquipmentId = eq.id;
        this.pendingEquipmentName = eq['listingName'] || eq.name || 'vehicle';
        this.openBuyMoreModal();
      } else {
        this.ux.showError('No marketplace slots remain. Please upgrade your plan to list more vehicles.');
      }
      return;
    }

    try {
      await updateDoc(doc(this.firestore, 'equipment-list', eq.id), {
        isMarketplaceActive: true,
        isListed: true,
        status: 'Active',
      });
      eq.isMarketplaceActive = true;
      eq.listingStatus = 'active';
      this.allEquipment = this.allEquipment.filter((item) => item.id !== eq.id);
      this.allEquipment.push({ ...eq, isMarketplaceActive: true, listingStatus: 'active' } as ListedEquipmentItem);
      this.allEquipment.sort((a, b) => (a.name || '').trim().toLowerCase().localeCompare((b.name || '').trim().toLowerCase()));
      ListedEquipmentComponent.cachedEquipment = [...this.allEquipment];
      this.applyFilters();
      this.ux.showSuccess('Vehicle is now visible in the marketplace.');
    } catch (e) {
      this.ux.showError('Failed to show vehicle. Please try again.');
    }
  }

  /**
   * Hides a vehicle from the marketplace and frees the slot.
   */
  async removeFromMarketplace(eq: ListedEquipmentItem): Promise<void> {
    if (eq.listingStatus === 'booked') return;

    const confirmed = await this.ux.showConfirmation(
      'This vehicle will be hidden from the marketplace. You can re-add it any time within your slot limit.',
      'Remove from Marketplace',
      'Remove',
      'Cancel',
    );
    if (!confirmed) return;

    try {
      await updateDoc(doc(this.firestore, 'equipment-list', eq.id), {
        isMarketplaceActive: false,
        isListed: false,
      });
      eq.isMarketplaceActive = false;
      eq['isListed'] = false;
      eq.listingStatus = 'inactive';
      this.allEquipment = this.allEquipment.filter((item) => item.id !== eq.id);
      ListedEquipmentComponent.cachedEquipment = [...this.allEquipment];
      this.applyFilters();
      this.ux.showSuccess('Vehicle removed from the marketplace and moved back to Manage Equipment.');
    } catch (e) {
      this.ux.showError('Failed to remove vehicle. Please try again.');
    }
  }

  // ── Buy More Slots modal ──────────────────────────────────────────────────

  async openBuyMoreModal(): Promise<void> {
    this.showBuyMoreModal = true;
    this.buyMorePaymentError = '';
    this.buyMorePaymentSuccess = false;
    this.isBuyMoreProcessing = false;
    this.isBuyMoreInitializing = false;
    this._buyMoreSubmitted = false;
    this._cardMounted = false;
    this._cardShouldMount = false;
    this._buyMoreClientSecret = '';
    this.buyMoreCount = 1;
    this._destroyCard();
  }

  closeBuyMoreModal(): void {
    if (this.isBuyMoreProcessing) return;
    this.showBuyMoreModal = false;
    this._buyMoreSubmitted = false;
    this._buyMoreClientSecret = '';
    this._destroyCard();
  }

  /** Fetch a PaymentIntent for the selected slot count */
  async initiateBuyMorePayment(): Promise<void> {
    if (this.isBuyMoreInitializing || this.isBuyMoreProcessing) return;
    this.isBuyMoreInitializing = true;
    this.buyMorePaymentError = '';
    this._cardMounted = false;
    this._cardShouldMount = false;
    this._buyMoreSubmitted = false;
    this._buyMoreClientSecret = '';
    this._destroyCard();

    try {
      if (!this.pendingEquipmentId) {
        throw new Error('No equipment selected for payment.');
      }
      const fn = httpsCallable(this.functions, 'activateListingIntent');
      const result: any = await fn({ equipmentIds: [this.pendingEquipmentId] });
      this._buyMoreClientSecret = result.data.clientSecret;
      await this._prepareStripeCard();
      this._cardShouldMount = true;
    } catch (err: any) {
      this.buyMorePaymentError = err?.message || 'Failed to initialize payment. Please try again.';
    } finally {
      this.isBuyMoreInitializing = false;
    }
  }

  async payBuyMoreSlots(): Promise<void> {
    if (this.isBuyMoreProcessing || this._buyMoreSubmitted) return;
    if (!this._stripe || !this._cardElement) return;
    if (!this._buyMoreClientSecret) {
      this.buyMorePaymentError = 'Payment session expired. Please go back and try again.';
      return;
    }

    this.isBuyMoreProcessing = true;
    this._buyMoreSubmitted = true;
    this.buyMorePaymentError = '';

    try {
      const { paymentIntent, error } = await this._stripe.confirmCardPayment(
        this._buyMoreClientSecret,
        { payment_method: { card: this._cardElement } },
      );

      if (error) {
        this.buyMorePaymentError = error.message || 'Payment failed. Please close and try again.';
        this.isBuyMoreProcessing = false;
        return;
      }

      if (paymentIntent?.status !== 'succeeded') {
        this.buyMorePaymentError = 'Payment did not complete. Please try again.';
        this.isBuyMoreProcessing = false;
        return;
      }

      // Confirm with backend — this will activate the pending listing.
      const confirmFn = httpsCallable(this.functions, 'confirmListingPayment');
      await confirmFn({ paymentIntentId: paymentIntent.id, equipmentIds: [this.pendingEquipmentId] });

      // Optimistically update local state
      const eq = this.allEquipment.find((item) => item.id === this.pendingEquipmentId);
      if (eq) {
        eq.isMarketplaceActive = true;
        eq.listingStatus = 'active';
      }
      this.buyMorePaymentSuccess = true;
      this.isBuyMoreProcessing = false;

      setTimeout(() => {
        this.closeBuyMoreModal();
        this.loadData(true);
      }, 2500);

    } catch (err: any) {
      console.error('Buy more slots payment error:', err);
      this.buyMorePaymentError = err?.message || 'Something went wrong. Please try again.';
      this.isBuyMoreProcessing = false;
    }
  }

  // ── Stripe helpers ────────────────────────────────────────────────────────

  private async _prepareStripeCard(): Promise<void> {
    if (!this._stripe) {
      this._stripe = await loadStripe(environment.stripePublishableKey);
    }
    const elements = this._stripe.elements();
    this._cardElement = elements.create('card', {
      style: {
        base: {
          fontFamily: "'Inter', system-ui, sans-serif",
          fontSize: '15px',
          color: '#0F172A',
          '::placeholder': { color: '#94A3B8' },
        },
        invalid: { color: '#EF4444' },
      },
    });
  }

  private _destroyCard(): void {
    if (this._cardElement) {
      try { this._cardElement.destroy(); } catch (_) { }
      this._cardElement = null;
    }
    this._cardMounted = false;
  }

  // ── Filters ───────────────────────────────────────────────────────────────

  private applyFilters(): void {
    let filtered = [...this.allEquipment];

    if (this.listingFilter !== 'all') {
      filtered = filtered.filter(e => e.listingStatus === this.listingFilter);
    }

    if (this.searchTerm) {
      const term = this.searchTerm.toLowerCase();
      filtered = filtered.filter(e =>
        (e.name || '').toLowerCase().includes(term) ||
        (e['code'] || '').toLowerCase().includes(term) ||
        (e['listingName'] || '').toLowerCase().includes(term) ||
        (e.model || '').toLowerCase().includes(term) ||
        (e.emirate || '').toLowerCase().includes(term),
      );
    }

    if (this.currentCategoryFilter) {
      filtered = filtered.filter(
        (eq: any) =>
          eq.categoryId === this.currentCategoryFilter ||
          eq.category === this.currentCategoryFilter ||
          eq.category?.id === this.currentCategoryFilter,
      );
    }

    if (this.filterEmirate) {
      filtered = filtered.filter(eq => eq.emirate === this.filterEmirate);
    }

    this.totalItems = filtered.length;
    const start = this.pageIndex * this.pageSize;
    this.dataSource = filtered.slice(start, start + this.pageSize);
  }

  handlePageEvent(e: any): void {
    if (e.pageSize !== this.pageSize) {
      this.pageSize = e.pageSize;
      this.pageIndex = 0;
    } else {
      this.pageIndex = e.pageIndex;
    }
    this.applyFilters();
  }

  setListingFilter(f: 'all' | 'active' | 'inactive' | 'booked'): void {
    this.listingFilter = f;
    this.pageIndex = 0;
    this.applyFilters();
  }

  applySearch(event: Event): void {
    this.searchTerm = (event.target as HTMLInputElement).value.trim().toLowerCase();
    this.pageIndex = 0;
    this.applyFilters();
  }

  recomputeCategoryCounts(): void {
    const counts: Record<string, number> = {};
    this.allEquipment.forEach((eq: any) => {
      const catId = eq.categoryId || (typeof eq.category === 'string' ? eq.category : eq.category?.id);
      if (catId) counts[catId] = (counts[catId] || 0) + 1;
    });
    this.categoryCounts = counts;
  }

  toggleFilterPanel(): void {
    this.showFilterPanel = !this.showFilterPanel;
    if (this.showFilterPanel) {
      this.tempCategoryFilter = this.currentCategoryFilter;
      this.tempEmirateFilter = this.filterEmirate;
      this.tempListingFilter = this.listingFilter;
      this.showAllCategories = false;
    }
  }

  selectCategory(catId: string): void { this.tempCategoryFilter = catId; }
  selectEmirate(emirate: string): void { this.tempEmirateFilter = emirate; }
  selectListingStatus(status: 'all' | 'active' | 'inactive' | 'booked'): void {
    this.tempListingFilter = status;
  }

  clearAllTempFilters(): void {
    this.tempCategoryFilter = '';
    this.tempEmirateFilter = '';
    this.tempListingFilter = 'all';
  }

  closeFilterPanelWithoutApplying(): void { this.showFilterPanel = false; }

  applyFiltersPopup(): void {
    this.currentCategoryFilter = this.tempCategoryFilter;
    this.filterEmirate = this.tempEmirateFilter;
    this.listingFilter = this.tempListingFilter;
    this.pageIndex = 0;
    this.applyFilters();
    this.showFilterPanel = false;
  }

  resetFiltersPopup(): void {
    this.tempCategoryFilter = '';
    this.tempEmirateFilter = '';
    this.tempListingFilter = 'all';
    this.currentCategoryFilter = '';
    this.filterEmirate = '';
    this.listingFilter = 'all';
    this.searchTerm = '';
    this.pageIndex = 0;
    this.applyFilters();
    this.showFilterPanel = false;
  }

  get tempActiveFilterCount(): number {
    let count = 0;
    if (this.tempCategoryFilter) count++;
    if (this.tempEmirateFilter) count++;
    if (this.tempListingFilter && this.tempListingFilter !== 'all') count++;
    return count;
  }

  get activeFilterCount(): number {
    let count = 0;
    if (this.currentCategoryFilter) count++;
    if (this.filterEmirate) count++;
    if (this.listingFilter && this.listingFilter !== 'all') count++;
    return count;
  }

  get availableCategories(): any[] {
    return this.categories.filter(cat => this.categoryCounts[cat.id] > 0);
  }

  clearFilters(): void {
    this.filterEmirate = '';
    this.currentCategoryFilter = '';
    this.searchTerm = '';
    this.listingFilter = 'all';
    this.pageIndex = 0;
    this.showFilterPanel = false;
    this.applyFilters();
  }

  getListingCount(status: string): number {
    return this.allEquipment.filter(e => e.listingStatus === status).length;
  }

  getCategoryIcon(name: string): string {
    const n = (name || '').toLowerCase();
    if (n.includes('crane')) return 'fa-solid fa-truck-pickup';
    if (n.includes('excavator')) return 'fa-solid fa-trowel';
    if (n.includes('forklift')) return 'fa-solid fa-dolly';
    if (n.includes('bus')) return 'fa-solid fa-bus';
    if (n.includes('loader')) return 'fa-solid fa-truck-tractor';
    if (n.includes('truck')) return 'fa-solid fa-truck';
    if (n.includes('generator')) return 'fa-solid fa-bolt';
    if (n.includes('compactor')) return 'fa-solid fa-road';
    return 'fa-solid fa-toolbox';
  }

  getListingLabel(eq: ListedEquipmentItem): string {
    const map: Record<string, string> = {
      active: 'In Marketplace',
      inactive: 'Not Listed',
      booked: 'Booked',
    };
    return map[eq.listingStatus || 'inactive'] || 'Not Listed';
  }

  getListingClass(eq: ListedEquipmentItem): string {
    return `le-badge--${eq.listingStatus || 'inactive'}`;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  openVehiclePlanDialog(): void {
    this.router.navigate(['/business/vehicle-plan']);
  }
}